import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { UserContext } from '../../context/UserContext';
import { useNavigate } from 'react-router-dom';
import './UserAdmin.css';
import HeaderImage from '../../assets/Images/Header.png';
import LottieLoader from '../LoadingSpinner/LottieLoader';

const UserAdmin = () => {
  const { currentUser } = useContext(UserContext);

  // Logo state
  const [logoFile, setLogoFile] = useState(null);
  const [squareLogoFile, setSquareLogoFile] = useState(null);
  const [liveLogoUrl, setLiveLogoUrl] = useState(null);
  const [liveSquareLogoUrl, setLiveSquareLogoUrl] = useState(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [uploadingMessage, setUploadingMessage] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser?.id) {
      fetchLatestLogos();
    }
  }, [currentUser]);

  const fetchLatestLogos = async () => {
    try {
      const res = await axios.get(
        `https://edugroove-backend.onrender.com/api/users/${currentUser.id}/logos`
      );

      setLiveLogoUrl(res.data.logoUrl || null);
      setLiveSquareLogoUrl(res.data.squareLogoUrl || null);
    } catch (err) {
      console.error('Error fetching logo:', err);
    }
  };

  const handleUploadLogo = async () => {
    if (!logoFile) return;

    const formData = new FormData();
    formData.append('logo', logoFile);

    try {
      setIsUploadingLogo(true);
      setUploadingMessage('Uploading rectangular logo…');

      await axios.post(
        `https://edugroove-backend.onrender.com/api/users/${currentUser.id}/logo`,
        formData
      );

      alert('Rectangular logo updated!');

      await fetchLatestLogos();
      setLogoFile(null);
    } catch (err) {
      console.error(err);
      alert('Failed to upload the rectangular logo.');
    } finally {
      setIsUploadingLogo(false);
      setUploadingMessage('');
    }
  };

  const handleUploadSquareLogo = async () => {
    if (!squareLogoFile) return;

    const formData = new FormData();
    formData.append('logo', squareLogoFile);

    try {
      setIsUploadingLogo(true);
      setUploadingMessage('Uploading square logo…');

      await axios.post(
        `https://edugroove-backend.onrender.com/api/users/${currentUser.id}/squareLogo`,
        formData
      );

      alert('Square logo updated!');

      await fetchLatestLogos();
      setSquareLogoFile(null);
    } catch (err) {
      console.error(err);
      alert('Failed to upload the square logo.');
    } finally {
      setIsUploadingLogo(false);
      setUploadingMessage('');
    }
  };

  if (!currentUser) {
    return <div className="user-admin-loading">Loading…</div>;
  }

  if (isUploadingLogo) {
    return (
      <div className="user-admin-page">
        <div className="user-admin-container user-admin-loader-panel">
          <LottieLoader message={uploadingMessage || 'Uploading…'} />
        </div>
      </div>
    );
  }

  return (
    <div className="user-admin-page">
      <div className="user-admin-container">
        <header className="user-admin-header">
          <button
            type="button"
            className="home-button"
            onClick={() => navigate('/splash')}
          >
            <span aria-hidden="true">←</span>
            Back to Home
          </button>

          <img
            src={HeaderImage}
            alt="EDU Groove"
            className="user-admin-header-image"
          />

          <div className="user-admin-title-group">
            <span className="user-admin-eyebrow">School Admin</span>

            <h1>School Branding</h1>

            <p>
              Add your school logos so exported resources feel consistent and
              ready to share.
            </p>
          </div>
        </header>

        <section
          className="branding-tutorial"
          aria-labelledby="branding-help-title"
        >
          <div className="branding-tutorial-icon" aria-hidden="true">
            i
          </div>

          <div>
            <h2 id="branding-help-title">How school branding works</h2>

            <p>
              Choose an image for each logo type, then press its upload button.
              The rectangular logo is best for wide headers, while the square
              logo is used in smaller or more compact spaces. Your new logo
              replaces the current one as soon as the upload finishes.
            </p>
          </div>
        </section>

        <section
          className="logo-upload-section"
          aria-labelledby="logo-section-title"
        >
          <div className="section-heading">
            <div>
              <span className="section-kicker">Brand assets</span>
              <h2 id="logo-section-title">Update Your Logos</h2>
            </div>

            <span className="section-status">2 logo formats</span>
          </div>

          <div className="logo-card-grid">
            <article className="logo-upload-card">
              <div className="logo-card-heading">
                <div>
                  <h3>Rectangular Logo</h3>
                  <p>Recommended size: 600 × 150 pixels</p>
                </div>

                <span className="logo-shape-badge logo-shape-wide">
                  Wide
                </span>
              </div>

              <div className="logo-preview logo-preview-wide">
                {liveLogoUrl ? (
                  <img
                    src={liveLogoUrl}
                    alt="Current rectangular school logo"
                    className="logo-thumbnail logo-thumbnail-wide"
                  />
                ) : (
                  <div className="logo-empty-state">
                    <span aria-hidden="true">▭</span>
                    <p>No rectangular logo uploaded</p>
                  </div>
                )}
              </div>

              <div className="logo-upload-controls">
                <label className="file-picker">
                  <span className="file-picker-button">Choose Image</span>

                  <span className="file-picker-name">
                    {logoFile ? logoFile.name : 'No file selected'}
                  </span>

                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      setLogoFile(e.target.files?.[0] || null)
                    }
                  />
                </label>

                <button
                  type="button"
                  className="upload-logo-button"
                  onClick={handleUploadLogo}
                  disabled={!logoFile}
                >
                  Upload Rectangular Logo
                </button>
              </div>
            </article>

            <article className="logo-upload-card">
              <div className="logo-card-heading">
                <div>
                  <h3>Square Logo</h3>
                  <p>Recommended size: 200 × 200 pixels</p>
                </div>

                <span className="logo-shape-badge logo-shape-square">
                  Square
                </span>
              </div>

              <div className="logo-preview logo-preview-square">
                {liveSquareLogoUrl ? (
                  <img
                    src={liveSquareLogoUrl}
                    alt="Current square school logo"
                    className="logo-thumbnail logo-thumbnail-square"
                  />
                ) : (
                  <div className="logo-empty-state">
                    <span aria-hidden="true">□</span>
                    <p>No square logo uploaded</p>
                  </div>
                )}
              </div>

              <div className="logo-upload-controls">
                <label className="file-picker">
                  <span className="file-picker-button">Choose Image</span>

                  <span className="file-picker-name">
                    {squareLogoFile
                      ? squareLogoFile.name
                      : 'No file selected'}
                  </span>

                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      setSquareLogoFile(e.target.files?.[0] || null)
                    }
                  />
                </label>

                <button
                  type="button"
                  className="upload-logo-button"
                  onClick={handleUploadSquareLogo}
                  disabled={!squareLogoFile}
                >
                  Upload Square Logo
                </button>
              </div>
            </article>
          </div>
        </section>
      </div>
    </div>
  );
};

export default UserAdmin;